import React, { FunctionComponent, useCallback } from "react";
import { useIntl } from "react-intl";
import CartInput from "@Components/atoms/cart/CartInput";
import CartAddressContainer from "@Components/molecules/cart/CartAddressContainer";
import CartAutoCompleteStreetInput from "@Components/molecules/cart/CartAddressForm/CartAutoCompleteStreetInput";
import CartCountrySelect from "@Components/molecules/cart/CartCountrySelect";
import { countries as countriesList } from "@Samples/mocks/cart";
import { AutoCompletePlace } from "@Types/addresses";
import { Country } from "@Types/cart";
import { userNamePattern } from "@Utils/checkFormats";
import "./style.scss";

interface Props {
  city: string | undefined;
  company: string | undefined;
  countries: Country[];
  country: string | undefined;
  details: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  onChange: (field: string, value: string) => void;
  postcode: string | undefined;
  street: string | undefined;
  tvaNumber: string | undefined;
}

const CartAddressFormBilling: FunctionComponent<Props> = ({
  city,
  company,
  country,
  details,
  firstName,
  lastName,
  postcode,
  street,
  tvaNumber,
  countries = [],
  onChange,
}) => {
  const intl = useIntl();

  const handleSelectStreet = useCallback(
    (place: AutoCompletePlace) => {
      const autoCompleteStreet =
        place.street_number !== ""
          ? `${place.street_number || ""} ${place.route || ""}`
          : `${place.route || ""}`;

      const selectedCountry = countriesList.find((value) => value.tag === place.country);

      onChange("city", place.locality || "");
      onChange("country", (selectedCountry && selectedCountry.name) || "");
      onChange("countryTag", (selectedCountry && selectedCountry.tag) || "");
      onChange("countryTag2", (selectedCountry && selectedCountry.tagAlpha3) || "");
      onChange("postcode", place.postal_code || "");
      onChange("street", autoCompleteStreet);
    },
    [onChange]
  );

  const handleChangeAddress = useCallback(
    (value) => {
      onChange("street", value);
    },
    [onChange]
  );

  const handleSelectCountry = useCallback(
    (value: Country) => {
      onChange("country", value.name);
      onChange("countryTag", value.tag);
      onChange("countryTag2", value.tagAlpha3);
    },
    [onChange]
  );

  return (
    <CartAddressContainer type="billing">
      <div className="CartAddressFormBilling-fieldLine">
        <div className="CartAddressFormBilling-inputContainer">
          <CartInput
            autocomplete="billing given-name"
            name="firstNameBilling"
            required={true}
            value={firstName}
            placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_firstName" })}
            pattern={userNamePattern}
            onChange={(value) => {
              onChange("firstName", value);
            }}
          />
        </div>
        <div className="CartAddressFormBilling-inputContainer">
          <CartInput
            autocomplete="billing family-name"
            name="lastNameBilling"
            value={lastName}
            placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_lastName" })}
            required={true}
            pattern={userNamePattern}
            onChange={(value) => {
              onChange("lastName", value);
            }}
          />
        </div>
        <div className="CartAddressFormBilling-inputContainer" />
      </div>
      <div className="CartAddressFormBilling-fieldLine">
        <CartInput
          autocomplete="billing organization"
          name="companyNameBilling"
          value={company}
          placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_company" })}
          onChange={(value) => {
            onChange("company", value);
          }}
        />
      </div>
      <div className="CartAddressFormBilling-fieldLine">
        <CartInput
          name="tvaNumber"
          value={tvaNumber}
          placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_tvaNumber" })}
          onChange={(value) => {
            onChange("tvaNumber", value);
          }}
        />
      </div>
      <div className="CartAddressFormBilling-fieldLine">
        <CartAutoCompleteStreetInput
          onChange={handleChangeAddress}
          onSelect={handleSelectStreet}
          value={street}
        />
      </div>
      <div className="CartAddressFormBilling-fieldLine">
        <CartInput
          autocomplete="billing address-line1"
          name="additionalDetailsBilling"
          value={details}
          placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_details" })}
          onChange={(value) => {
            onChange("details", value);
          }}
        />
      </div>
      <div className="CartAddressFormBilling-fieldLine">
        <div className="CartAddressFormBilling-inputContainer">
          <CartInput
            autocomplete="billing postal-code"
            name="postcodeBilling"
            value={postcode}
            placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_postcode" })}
            required={true}
            onChange={(value) => {
              onChange("postcode", value);
            }}
          />
        </div>
        <div className="CartAddressFormBilling-inputContainer">
          <CartInput
            name="cityBilling"
            value={city}
            placeholder={intl.formatMessage({ id: "cheerz_CartAddressForm_city" })}
            required={true}
            onChange={(value) => {
              onChange("city", value);
            }}
          />
        </div>
        <div className="CartAddressFormBilling-countrySelectContainer">
          <CartCountrySelect
            countries={countries}
            onSelect={handleSelectCountry}
            selectedCountry={country}
          />
        </div>
      </div>
      <div className="CartAddressFormBilling-billingInfosContainer">
        {intl.formatMessage({ id: "cheerz_CartAddressForm_billing_description" })}
      </div>
    </CartAddressContainer>
  );
};

export default CartAddressFormBilling;
